import { useCollections } from "@src/graphql/hooks/useCollections";
import { useProductTags } from "@src/graphql/hooks/useProductTags";
import { useProductTypes } from "@src/graphql/hooks/useProductTypes";
import { useProductVendors } from "@src/graphql/hooks/useProductVendors";

import { ShopifyItemPickerItem } from "./ShopifyItemPicker";

export type ShopifyItemKind = "tag" | "collection" | "vendor" | "product_type";

/**
 * Loads the full store-wide list of one flat Shopify item kind for the
 * ShopifyItemPicker. Collections are identified by GID; tags, vendors and
 * product types by their own string. Items stream in page by page (`loading` is true until the
 * first page lands), so mount this only while the picker is open.
 */
export function useShopifyPickerItems(kind: ShopifyItemKind): {
  items: ShopifyItemPickerItem[];
  loading: boolean;
} {
  const tagsData = useProductTags({ skip: kind !== "tag" });
  const vendorsData = useProductVendors({ skip: kind !== "vendor" });
  const typesData = useProductTypes({ skip: kind !== "product_type" });
  const collectionsData = useCollections({ skip: kind !== "collection" });

  switch (kind) {
    case "tag":
      return {
        items: (tagsData?.productTags?.edges ?? []).map(({ node }) => ({
          id: node,
          label: node,
        })),
        loading: !tagsData,
      };
    case "vendor":
      return {
        items: (vendorsData?.productVendors?.edges ?? []).map(({ node }) => ({
          id: node,
          label: node,
        })),
        loading: !vendorsData,
      };
    case "product_type":
      return {
        items: (typesData?.productTypes?.edges ?? []).map(({ node }) => ({
          id: node,
          label: node,
        })),
        loading: !typesData,
      };
    case "collection":
      return {
        items: (collectionsData?.collections?.edges ?? []).map(({ node }) => ({
          id: node.id,
          label: node.title,
        })),
        loading: !collectionsData,
      };
  }
}
